import React, { useState, useEffect } from 'react';
import { BiCheckbox } from 'react-icons/bi';
import { FiSearch } from 'react-icons/fi';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';

const API_BASE_URL = "https://backend.dlapakistan.com/api";

function DentalSidebar() {
  const navigate = useNavigate();
  const location = useLocation();
  
  // State for dental news items from backend
  const [dentalNewsItems, setDentalNewsItems] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // Utility function to extract data from different API response structures
  const extractDataFromResponse = (response) => {
    if (!response || !response.data) return [];
    
    const data = response.data;
    
    if (Array.isArray(data)) {
      return data;
    } else if (data.results && Array.isArray(data.results)) {
      return data.results;
    } else if (data.data && Array.isArray(data.data)) {
      return data.data;
    } else if (data.items && Array.isArray(data.items)) {
      return data.items;
    } else {
      console.warn("Unexpected API response structure:", data);
      return [];
    }
  };

  // Fetch dental news items from backend
  const fetchDentalNews = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await axios.get(`${API_BASE_URL}/dental-news/`, {
        params: {
          is_published: 'true',
        }
      });
      
      console.log("Sidebar API Response:", response.data);
      
      // Extract data using utility function
      const newsData = extractDataFromResponse(response);
      
      // Filter only published news items for public website
      const publishedNews = newsData.filter(item => item.is_published === true);
      
      // Sort by created_at date (oldest first)
      const sortedNews = [...publishedNews].sort((a, b) => 
        new Date(a.created_at) - new Date(b.created_at)
      );
      
      setDentalNewsItems(sortedNews);
    } catch (err) {
      console.error("Error fetching dental news:", err);
      console.error("Error details:", err.response?.data || err.message);
      setError("Failed to load news categories");
      setDentalNewsItems([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch data on component mount
  useEffect(() => {
    fetchDentalNews();
  }, []);

  const handleSidebarClick = (id) => {
    // Navigate to the posts page for this news item
    navigate(`/dental/${id}/posts`);
  };

  const isActivePath = (id) => {
    // Check if we're on the posts page for this specific news item
    const currentPath = location.pathname;
    return currentPath === `/dental/${id}/posts` ||
           currentPath.startsWith(`/dental/${id}/`);
  };

  return (
    <div className="w-full bg-[#f7f9fc] p-4 md:p-5 rounded-lg shadow-sm h-fit top-52 overflow-x-auto">
      <div className="bg-[#70B444] text-white p-3 text-center mb-4 rounded">
        <h3 className="text-base lg:text-lg font-semibold">
          Dental News Categories
        </h3>
      </div>

      {/* Categories List */}
      <div className="space-y-0 max-h-[500px] overflow-y-auto overflow-x-hidden pr-1">
        {isLoading ? (
          <div className="text-center py-4 text-gray-500">
            <div className="inline-block animate-spin rounded-full h-5 w-5 border-b-2 border-[#70B444] mr-2"></div>
            Loading categories...
          </div>
        ) : error ? (
          <div className="text-center py-4 text-red-500 text-sm bg-red-50 p-3 rounded">
            {error}
            <button 
              onClick={fetchDentalNews}
              className="mt-2 text-xs underline hover:text-red-700 block w-full"
            >
              Try again
            </button>
          </div>
        ) : dentalNewsItems.length === 0 ? (
          <div className="text-center py-4 text-gray-500 bg-gray-50 p-3 rounded">
            No dental news categories available
          </div>
        ) : (
          dentalNewsItems.map((item, index) => (
            <div
              key={item.id}
              onClick={() => handleSidebarClick(item.id)}
              className={`flex items-center justify-between py-2.5 px-2 text-sm lg:text-base cursor-pointer
                text-[#70B444] font-medium hover:bg-[rgba(112,180,68,0.1)] transition-colors
                ${
                  index < dentalNewsItems.length - 1
                    ? "border-b border-gray-200"
                    : ""
                }
                ${
                  isActivePath(item.id)
                    ? "bg-[rgba(112,180,68,0.1)] border-l-4 border-l-[#70B444] pl-1.5"
                    : ""
                }`}
            >
              <div className="flex items-center flex-1 min-w-0">
                <BiCheckbox className="mr-2 text-lg flex-shrink-0 min-w-[24px]" />
                <span className="break-words leading-tight truncate" title={item.title}>
                  {item.title}
                </span>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Archives - Optional */}
      <div className="bg-[#70B444] text-white p-3 text-center mt-6 mb-4 rounded">
        <h3 className="text-base lg:text-lg font-semibold">
          News Archives
        </h3>
      </div>

      <div className="mb-4 px-2 overflow-x-hidden">
        <select className="w-full border border-gray-300 px-3 py-2.5 text-sm rounded focus:outline-none focus:ring-2 focus:ring-[#70B444] bg-white">
          <option>Select Year</option>
          <option>2025</option>
          <option>2024</option>
          <option>2023</option>
          <option>2022</option>
        </select>
      </div>

      {/* Search - Optional */}
      <div className="bg-[#70B444] text-white p-3 text-center mt-6 mb-4 rounded">
        <h3 className="text-base lg:text-lg font-semibold">
          Search News
        </h3>
      </div>

      <div className="px-2 overflow-x-hidden">
        <div className="relative">
          <input
            type="text"
            placeholder="Search ..."
            className="w-full border border-gray-300 px-3 py-2.5 pr-10 text-sm rounded focus:outline-none focus:ring-2 focus:ring-[#70B444]"
          />
          <FiSearch className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500" />
        </div>
      </div>
    </div>
  );
}

export default DentalSidebar;